import React, { useEffect, useState, useContext } from 'react';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Grid, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Divider
} from '@material-ui/core';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import { format, subDays } from 'date-fns';
import { toast } from 'react-toastify';
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
    headerContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: "16px",
    },
    buttonFilter: {
        border: `1px solid ${theme.mode === 'light' ? '#e5e5e5' : '#1D1D1F'}`,
        color: theme.mode === 'light' ? '#1D1D1F' : '#c0cad9',
        lineHeight: 1,
    },
    tableHeadCell: {
        fontWeight: "bold",
        backgroundColor: theme.mode === 'light' ? '#f9fafb' : '#1e1e1e',
    },
    highlightValue: {
        fontWeight: 700,
        color: theme.palette.primary.main,
    }
}));

const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

export default function TableUserValues() {
    const classes = useStyles();
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(subDays(new Date(), 30));
    const [finalDate, setFinalDate] = useState(new Date());
    const [openModal, setOpenModal] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const { user } = useContext(AuthContext);

    const companyId = user?.companyId;

    useEffect(() => {
        if (companyId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const fetchData = async () => {
        try {
            const { data } = await api.get(`/dashboard/usersSalesValues`, {
                params: {
                    initialDate: format(initialDate, 'yyyy-MM-dd'),
                    finalDate: format(finalDate, 'yyyy-MM-dd'),
                    companyId
                }
            });
            setUsersData(data.data || []);
        } catch (error) {
            console.error("Erro ao buscar dados de vendas dos usuários:", error);
            toast.error('Erro ao buscar desempenho comercial dos usuários');
        }
    };

    return (
        <>
            <div className={classes.headerContainer}>
                <Typography component="h2" variant="h6" color="primary" gutterBottom style={{ margin: 0, fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
                    Desempenho Comercial
                </Typography>
                <Button className={classes.buttonFilter} onClick={() => setOpenModal(true)}>
                    <i className="ri-filter-3-line" style={{ marginRight: 8 }}></i> Filtros
                </Button>
            </div>

            <TableContainer style={{ borderTop: `1px solid ${theme.mode === "light" ? "#f3f4f6" : "#121216"}` }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                            <TableCell align="center" className={classes.tableHeadCell}>Vendas</TableCell>
                            <TableCell align="center" className={classes.tableHeadCell}>Orçamento</TableCell>
                            <TableCell align="center" className={classes.tableHeadCell}>Ativos</TableCell>
                            <TableCell align="center" className={classes.tableHeadCell}>Passivos</TableCell>
                            <TableCell align="center" className={classes.tableHeadCell}>Atendimentos</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usersData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">Nenhum dado encontrado para o período.</TableCell>
                            </TableRow>
                        ) : (
                            usersData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="center">
                                        <span className={classes.highlightValue}>{formatCurrency(row.closedValue)}</span> 
                                        <span style={{ fontSize: '0.8rem', color: 'gray', marginLeft: 4 }}>({row.closedCount})</span>
                                    </TableCell>
                                    <TableCell align="center">{formatCurrency(row.inBudget)}</TableCell>
                                    <TableCell align="center">{formatCurrency(row.activeLeads)}</TableCell>
                                    <TableCell align="center">{formatCurrency(row.passiveLeads)}</TableCell>
                                    <TableCell align="center">{row.totalAttendances}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Filtro Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Filtros de Período</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                                <DatePicker
                                    value={initialDate}
                                    onChange={(newValue) => setInitialDate(newValue)}
                                    label={i18n.t("dashboard.date.initialDate")}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                                <DatePicker
                                    value={finalDate}
                                    onChange={(newValue) => setFinalDate(newValue)}
                                    label={i18n.t("dashboard.date.finalDate")}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Divider style={{ margin: '24px 0' }} />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Data atual: {format(new Date(), 'dd/MM/yyyy')}
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: 16 }}>
                    <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
                    <Button 
                        onClick={() => { 
                            fetchData(); 
                            setOpenModal(false); 
                        }} 
                        color="primary" 
                        variant="contained" 
                        style={{ color: '#fff' }}
                    >
                        Aplicar Filtro
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}